import { useState } from "react";
import { PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { useInView } from "@/hooks/useInView";
import axios from "axios";
import { getTrackingData } from "@/lib/tracking";
import { postToGHL, ghlKeyForInquiry, buildGhlPayload } from "@/lib/ghl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  { value: "private", label: "Private Coaching" },
  { value: "youth", label: "Youth Readiness" },
  { value: "workshop", label: "Self-Defense Workshop" },
  { value: "org", label: "Organizational or Community Workshop" },
  { value: "corporate", label: "Corporate Workshop" },
  { value: "executive", label: "3SM Executive Composure\u2122" },
  { value: "smallgroup", label: "Small-Group Training" },
  { value: "striking", label: "Striking or Boxing Coaching" },
  { value: "strength", label: "Strength and Conditioning" },
  { value: "other", label: "General Inquiry" },
];

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service_type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [ref, inView] = useInView();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Phone validation — at least 10 digits
    const phoneDigits = (form.phone || "").replace(/\D/g, "");
    if (phoneDigits.length < 10) {
      setError("Please enter your phone number so we can follow up with you.");
      setLoading(false);
      return;
    }

    const tracking = getTrackingData();

    try {
      // 1. Save to backend (MongoDB)
      await axios.post(`${API}/inquiries`, { ...form, tracking });

      // 2. POST directly to the correct GoHighLevel webhook based on service_type
      const ghlKey = ghlKeyForInquiry(form.service_type);
      if (ghlKey) {
        try {
          await postToGHL(ghlKey, buildGhlPayload({
            formType: "contact_inquiry",
            name: form.name,
            email: form.email,
            phone: form.phone,
            message: form.message,
            extras: { service_type: form.service_type },
            tracking,
          }));
        } catch (ghlErr) {
          console.error("GoHighLevel webhook error:", ghlErr);
        }
      }

      setSuccess(true);
      setForm({ name: "", email: "", phone: "", service_type: "", message: "" });
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "bg-[#1A1A1A]/80 backdrop-blur-sm border border-white/10 rounded-md px-4 py-3 text-[#F2EFE9] placeholder:text-[#8C8C8C]/60 font-body text-sm focus:ring-1 focus:ring-[#D4AF37] focus:border-[#D4AF37] outline-none w-full transition-all focus:bg-[#1A1A1A]";

  if (success) {
    return (
      <section id="contact" data-testid="contact-section" className="bg-[#050505] py-16 sm:py-20">
        <div className="max-w-2xl mx-auto px-6 lg:px-8 text-center">
          <div className="animate-float">
            <CheckCircle size={48} weight="duotone" className="text-[#D4AF37] mx-auto mb-4" />
          </div>
          <h2 data-testid="contact-success-heading" className="font-heading text-2xl font-bold text-[#F2EFE9] mb-2">Message Received</h2>
          <p className="text-[#8C8C8C] font-body text-sm">We'll be in touch within 24 hours. Thank you for reaching out.</p>
          <button
            data-testid="contact-send-another"
            onClick={() => setSuccess(false)}
            className="mt-6 text-[#D4AF37] text-sm font-bold tracking-wider uppercase hover:text-[#E5C158] transition-colors"
          >
            Send Another Message
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#050505] py-16 sm:py-20" ref={ref}>
      <div className="max-w-2xl mx-auto px-6 lg:px-8">
        <p className={`text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] font-body mb-4 reveal ${inView ? 'visible' : ''}`}>Get In Touch</p>
        <h2 className={`font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#F2EFE9] mb-4 reveal ${inView ? 'visible' : ''} stagger-1`}>
          Ready to start?
        </h2>
        <p className={`text-[#8C8C8C] font-body text-sm mb-10 reveal ${inView ? 'visible' : ''} stagger-2`}>
          Book a session, reserve a workshop spot, or ask us anything. Call{' '}
          <a href="tel:2146006027" className="text-[#D4AF37] hover:text-[#E5C158] transition-colors">(214) 600-6027</a>{' '}
          or fill out the form below.
        </p>

        <form onSubmit={handleSubmit} data-testid="contact-form" className={`space-y-4 reveal ${inView ? 'visible' : ''} stagger-3`}>
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="contact-input-name" required placeholder="Your Name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} />
            <input data-testid="contact-input-email" required type="email" placeholder="Email Address" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="contact-input-phone" required type="tel" inputMode="tel" placeholder="Phone Number" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputClass} />
            <select data-testid="contact-select-service" required value={form.service_type}
              onChange={(e) => setForm({ ...form, service_type: e.target.value })}
              className={`${inputClass} ${!form.service_type ? 'text-[#8C8C8C]/60' : ''}`}>
              <option value="">Select Service</option>
              {services.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
            </select>
          </div>
          <textarea data-testid="contact-input-message" required placeholder="Tell us about your goals..." value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} className={inputClass} />
          {error && <p data-testid="contact-error" className="text-red-400 text-sm font-body">{error}</p>}
          <button data-testid="contact-submit-btn" type="submit" disabled={loading}
            className="bg-[#D4AF37] text-[#050505] font-bold px-8 py-4 rounded-md hover:bg-[#E5C158] transition-all uppercase tracking-widest text-sm flex items-center gap-2 disabled:opacity-50 hover:shadow-[0_0_20px_rgba(212,175,55,0.3)]">
            <PaperPlaneTilt size={18} weight="bold" />
            {loading ? "Sending..." : "Send Message"}
          </button>
        </form>
      </div>
    </section>
  );
}
